// /src/GuitarSimulator/audioAnalyzer.js

export class AudioAnalyzer {
  constructor() {
    this.audioContext = null;
    this.analyser = null;
    this.microphone = null;
    this.dataArray = null;
    this.bufferLength = null;
    this.isActive = false;
    this.audioElement = null;
    this.sensitivity = 10; // 기본 민감도 10%
    
    // 기타 주파수 범위 (기본적인 기타 음역대)
    this.guitarFrequencies = {
      'E2': 82.41, 'F2': 87.31, 'F#2': 92.50, 'G2': 98.00, 'G#2': 103.83, 
      'A2': 110.00, 'A#2': 116.54, 'B2': 123.47, 'C3': 130.81, 'C#3': 138.59, 
      'D3': 146.83, 'D#3': 155.56, 'E3': 164.81, 'F3': 174.61, 'F#3': 185.00, 
      'G3': 196.00, 'G#3': 207.65, 'A3': 220.00, 'A#3': 233.08, 'B3': 246.94,
      'C4': 261.63, 'C#4': 277.18, 'D4': 293.66, 'D#4': 311.13, 'E4': 329.63, 
      'F4': 349.23, 'F#4': 369.99, 'G4': 392.00, 'G#4': 415.30, 'A4': 440.00, 
      'A#4': 466.16, 'B4': 493.88, 'C5': 523.25, 'C#5': 554.37, 'D5': 587.33,
      'D#5': 622.25, 'E5': 659.25, 'F5': 698.46, 'F#5': 739.99, 'G5': 783.99
    };
  }

  // 마이크로폰으로부터 오디오 입력 초기화
  async initMicrophone() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false
        } 
      });
      
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      this.microphone = this.audioContext.createMediaStreamSource(stream);
      this.setupAnalyzer();
      
      return true;
    } catch (error) {
      console.error('마이크 초기화 실패:', error);
      return false;
    }
  }

  // MP3 파일 분석 초기화
  async initFileAnalysis(file) {
    try {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      const arrayBuffer = await file.arrayBuffer();
      const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
      
      const source = this.audioContext.createBufferSource();
      source.buffer = audioBuffer;
      
      this.setupAnalyzer();
      source.connect(this.analyser);
      source.start();
      
      return true;
    } catch (error) {
      console.error('파일 분석 초기화 실패:', error);
      return false;
    }
  }

  // 민감도 설정 메서드
  setSensitivity(sensitivity) {
    this.sensitivity = Math.max(0, Math.min(100, sensitivity));
  }

  // 분석기 설정
  setupAnalyzer() {
    this.analyser = this.audioContext.createAnalyser();
    this.analyser.fftSize = 4096; // 높은 해상도
    this.analyser.smoothingTimeConstant = 0.3;
    
    this.bufferLength = this.analyser.frequencyBinCount;
    this.dataArray = new Uint8Array(this.bufferLength);
    
    if (this.microphone) {
      this.microphone.connect(this.analyser);
    }
  }

  // 주파수 분석 및 음표별 강도 계산
  analyzeFrequencies() {
    if (!this.analyser || !this.isActive) return {};

    this.analyser.getByteFrequencyData(this.dataArray);
    
    const sampleRate = this.audioContext.sampleRate;
    const frequencyResolution = sampleRate / (this.analyser.fftSize * 2);
    
    const noteIntensities = {};
    
    // 각 기타 음표의 주파수 대역에서 강도 측정
    Object.entries(this.guitarFrequencies).forEach(([note, frequency]) => {
      const binIndex = Math.round(frequency / frequencyResolution);
      
      // 주변 빈들의 평균으로 더 정확한 측정
      let intensity = 0;
      let count = 0;
      
      for (let i = Math.max(0, binIndex - 2); i <= Math.min(this.bufferLength - 1, binIndex + 2); i++) {
        intensity += this.dataArray[i];
        count++;
      }
      
      // 0-1 범위로 정규화하고 민감도 적용
      let normalizedIntensity = count > 0 ? (intensity / count) / 255 : 0;

      // 민감도 임계값 적용 (민감도가 낮을수록 높은 임계값)
      const threshold = (100 - this.sensitivity) / 100 * 0.3; // 0~0.3 범위
      noteIntensities[note] = normalizedIntensity > threshold ? normalizedIntensity : 0;
    });
    
    return noteIntensities;
  }

  // 분석 시작
  start() {
    this.isActive = true;
  }

  // 분석 중지
  stop() {
    this.isActive = false;
    
    // 마이크 스트림 정지
    if (this.microphone && this.microphone.mediaStream) {
      this.microphone.mediaStream.getTracks().forEach(track => track.stop());
    }
    
    // 오디오 파일 재생 정지
    if (this.audioElement) {
      this.audioElement.pause();
      this.audioElement.currentTime = 0;
      URL.revokeObjectURL(this.audioElement.src); // 메모리 해제
      this.audioElement = null;
    }
    
    // 자체 생성한 AudioContext만 닫기
    if (this.audioContext && this.createdOwnContext && this.audioContext.state !== 'closed') {
      this.audioContext.close();
    }
  }

  // 파일 재생 제어 메서드들
  pauseFile() {
    if (this.audioElement && !this.audioElement.paused) {
      this.audioElement.pause();
    }
  }

  resumeFile() {
    if (this.audioElement && this.audioElement.paused) {
      this.audioElement.play().catch(console.error);
    }
  }

  setFileVolume(volume) {
    if (this.audioElement) {
      this.audioElement.volume = Math.max(0, Math.min(1, volume / 100));
    }
  }

  seekFile(time) {
    if (this.audioElement) {
      this.audioElement.currentTime = time;
    }
  }

  getFileInfo() {
    if (this.audioElement) {
      return {
        duration: this.audioElement.duration || 0,
        currentTime: this.audioElement.currentTime || 0,
        paused: this.audioElement.paused,
        volume: this.audioElement.volume * 100
      };
    }
    return null;
  }

  // 스펙트럼 디스플레이용 raw 데이터 접근
  getSpectrumData() {
    if (!this.analyser || !this.dataArray) return null;
    
    this.analyser.getByteFrequencyData(this.dataArray);
    return {
      frequencyData: new Uint8Array(this.dataArray), // 복사본 반환
      bufferLength: this.bufferLength,
      sampleRate: this.audioContext.sampleRate,
      fftSize: this.analyser.fftSize
    };
  }

  // 시간 도메인 데이터 (파형)
  getTimeDomainData() {
    if (!this.analyser) return null;
    
    const timeDomainData = new Uint8Array(this.bufferLength);
    this.analyser.getByteTimeDomainData(timeDomainData);
    return timeDomainData;
  }

  // 실시간 분석 루프
  startAnalysisLoop(callback) {
    const analyze = () => {
      if (this.isActive) {
        const intensities = this.analyzeFrequencies();
        callback(intensities);
        requestAnimationFrame(analyze);
      }
    };
    analyze();
  }
}

// 유틸리티 함수들
export const AudioUtils = {
  // 주파수에서 음표 이름 추출
  frequencyToNote: (frequency) => {
    const A4 = 440;
    const semitone = Math.round(12 * Math.log2(frequency / A4));
    const noteNames = ['A', 'A#', 'B', 'C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#'];
    const octave = Math.floor(4 + (semitone + 9) / 12);
    const noteIndex = (semitone + 9) % 12;
    return noteNames[noteIndex] + octave;
  },

  // 강도를 색상 밝기로 변환 (개선된 버전 - 밝기와 색상 동시 조절)
  intensityToBrightness: (intensity, baseColor = '#7a7272') => {
    // 임계값 미만은 기본 색상으로 표시
    if (intensity < 0.15) {
      return baseColor;
    }
    
    // 강도를 0-1 범위로 재정규화 (0.15 이상을 0-1로 매핑)
    const normalizedIntensity = Math.min(1, (intensity - 0.15) / 0.85);
    
    // HSL 색상 시스템 사용 (색조, 채도, 밝기)
    let hue, saturation, lightness;
    
    if (normalizedIntensity < 0.3) {
      // 약한 신호: 파란색 → 청록색
      hue = 240 - (normalizedIntensity / 0.3) * 60; // 240도(파란색) → 180도(청록색)
      saturation = 40 + (normalizedIntensity / 0.3) * 30; // 40% → 70%
      lightness = 0 + (normalizedIntensity / 0.3) * 20; // 60% → 80%
    } else if (normalizedIntensity < 0.6) {
      // 중간 신호: 청록색 → 노란색
      const subIntensity = (normalizedIntensity - 0.3) / 0.3;
      hue = 180 - subIntensity * 120; // 180도(청록색) → 60도(노란색)
      saturation = 70 + subIntensity * 20; // 70% → 90%
      lightness = 80 - subIntensity * 20; // 80% → 60%
    } else {
      // 강한 신호: 노란색 → 빨간색
      const subIntensity = (normalizedIntensity - 0.6) / 0.4;
      hue = 60 - subIntensity * 60; // 60도(노란색) → 0도(빨간색)
      saturation = 90 + subIntensity * 10; // 90% → 100%
      lightness = 60 - subIntensity * 10; // 60% → 50% (더 어둡게)
    }
    
    // 전체적인 밝기 조절 (강도에 비례)
    const brightnessFactor = 0.3 + normalizedIntensity * 0.7; // 30% → 100%
    lightness = lightness * brightnessFactor;
    
    // HSL을 RGB로 변환
    const hslToRgb = (h, s, l) => {
      h = h / 360;
      s = s / 100;
      l = l / 100;
      
      const hue2rgb = (p, q, t) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1/6) return p + (q - p) * 6 * t;
        if (t < 1/2) return q;
        if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
        return p;
      };
      
      let r, g, b;
      
      if (s === 0) {
        r = g = b = l;
      } else {
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        r = hue2rgb(p, q, h + 1/3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1/3);
      }
      
      return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
    };
    
    const [r, g, b] = hslToRgb(hue, saturation, lightness);
    
    // 강도가 높을수록 글로우 효과 추가
    if (normalizedIntensity > 0.7) {
      return `rgb(${r}, ${g}, ${b})`;
    } else {
      return `rgb(${r}, ${g}, ${b})`;
    }
  },

  // 글로우 효과를 위한 추가 함수
  getGlowEffect: (intensity) => {
    if (intensity < 0.15) return 'none';
    
    const normalizedIntensity = Math.min(1, (intensity - 0.15) / 0.85);
    
    if (normalizedIntensity > 0.7) {
      const glowIntensity = (normalizedIntensity - 0.7) / 0.3;
      const shadowBlur = 5 + glowIntensity * 10;
      const shadowSpread = glowIntensity * 3;
      
      let glowColor;
      if (normalizedIntensity > 0.85) {
        glowColor = `255, ${255 - glowIntensity * 100}, ${255 - glowIntensity * 255}`; // 빨간색 글로우
      } else {
        glowColor = `255, 255, ${255 - glowIntensity * 100}`; // 노란색 글로우
      }
      
      return `0 0 ${shadowBlur}px ${shadowSpread}px rgba(${glowColor}, ${0.3 + glowIntensity * 0.4})`;
    }
    
    return 'none';
  }
};